import { useState, FormEvent } from "react";
import { Link } from "react-router-dom";

export default function FooterSection() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setSubmitted(true);
      return;
    }
    if (!email.trim()) return;
    setLoading(true);
    try {
      await fetch("https://readdy.ai/api/form/d8vrov8bhec1he3opelg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({ email }).toString(),
      });
    } catch (_) {
      // silently handle
    }
    setLoading(false);
    setSubmitted(true);
    setEmail("");
  };

  return (
    <footer className="relative w-full bg-foreground-950 rounded-t-3xl overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none select-none">
        <span className="absolute -bottom-8 left-0 text-[12rem] md:text-[16rem] font-heading font-extrabold text-foreground-900/15 leading-none whitespace-nowrap">
          CSSBUY
        </span>
      </div>

      <div className="relative z-10 px-6 md:px-12 lg:px-20 py-14 md:py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-12">
          {/* Column 1 - Brand */}
          <div className="lg:col-span-1">
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-6">
              CSSBuy<span className="text-accent-400">.</span>Team
            </h3>
            <p className="text-sm text-foreground-400 leading-relaxed max-w-xs">
              A <strong className="text-background-50/80">planilha CSSBuy</strong> mais completa para compradores do Brasil e Portugal. Produtos verificados, atualizações semanais e links organizados.
            </p>
          </div>

          {/* Column 2 - Navigation */}
          <div>
            <h4 className="text-sm font-semibold text-background-50 mb-5 whitespace-nowrap">Navegação</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="text-sm text-foreground-400 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer">
                  Home
                </Link>
              </li>
              <li>
                <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-sm text-foreground-400 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer">
                  Categorias
                </a>
              </li>
              <li>
                <Link to="/artigo/guia-cssbuy-sheet-2026" className="text-sm text-foreground-400 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer">
                  Blog
                </Link>
              </li>
              <li>
                <Link to="/artigo/tendencias-cssbuy-2026" className="text-sm text-foreground-400 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer">
                  Artigos SEO
                </Link>
              </li>
              <li>
                <Link to="/" className="text-sm text-foreground-400 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer">
                  Contato
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 3 - Social */}
          <div>
            <h4 className="text-sm font-semibold text-background-50 mb-5 whitespace-nowrap">Redes Sociais</h4>
            <ul className="space-y-3">
              <li>
                <a
                  href="https://instagram.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 text-sm text-foreground-400 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-instagram-line text-base w-5 h-5 flex items-center justify-center"></i>
                  Instagram
                </a>
              </li>
              <li>
                <a
                  href="https://youtube.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 text-sm text-foreground-400 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-youtube-line text-base w-5 h-5 flex items-center justify-center"></i>
                  YouTube
                </a>
              </li>
              <li>
                <a
                  href="https://facebook.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 text-sm text-foreground-400 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-facebook-line text-base w-5 h-5 flex items-center justify-center"></i>
                  Facebook
                </a>
              </li>
            </ul>
          </div>

          {/* Column 4 - Newsletter */}
          <div>
            <h4 className="text-sm font-semibold text-background-50 mb-5 whitespace-nowrap">
              Receba atualizações da planilha CSSBuy
            </h4>
            {submitted ? (
              <div className="flex items-center gap-2 text-accent-400 text-sm">
                <i className="ri-check-line text-lg w-5 h-5 flex items-center justify-center"></i>
                <span className="whitespace-nowrap">Inscrito com sucesso!</span>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-readdy-form className="flex gap-2">
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="website-alt-field"
                />
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Seu melhor e-mail"
                  required
                  className="flex-1 bg-foreground-800 text-background-50 text-sm px-4 py-3 rounded-full outline-none border border-foreground-700 focus:border-accent-500 transition-colors placeholder:text-foreground-500"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="w-11 h-11 rounded-full bg-accent-500 text-background-50 flex items-center justify-center flex-shrink-0 cursor-pointer transition-all hover:bg-accent-600 disabled:opacity-50"
                >
                  {loading ? (
                    <i className="ri-loader-4-line animate-spin text-sm w-5 h-5 flex items-center justify-center"></i>
                  ) : (
                    <i className="ri-arrow-right-line text-sm w-5 h-5 flex items-center justify-center"></i>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 md:mt-16 pt-6 border-t border-foreground-800/50 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-foreground-500 whitespace-nowrap">
            &copy; 2026 CSSBuy.Team — Todos os direitos reservados.
          </p>
          <p className="text-xs text-foreground-600 whitespace-nowrap">
            Planilha CSSBuy atualizada semanalmente para Brasil e Portugal.
          </p>
        </div>
      </div>
    </footer>
  );
}